import React, { useState } from "react";
import { Lifebuoy, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Floating "raise an issue to Clariva" button + drawer.
 * Visible for every logged-in tenant user (not Clariva staff — they have
 * the global admin console instead).
 */
export default function SupportWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", severity: "normal", category: "bug" });
  const [sending, setSending] = useState(false);

  if (!user) return null;
  if (user.is_global || ["global_admin", "clariva_support"].includes(user.role)) return null;

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Subject and description required");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/tickets", {
        ...form,
        page_url: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent,
      });
      toast.success("Ticket raised — the Clariva team will reach out.");
      setForm({ subject: "", description: "", severity: "normal", category: "bug" });
      setOpen(false);
    } catch (e) {
      toast.error("Could not submit — please try again");
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        data-testid="support-widget-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[var(--primary)] text-[var(--primary-fg)] shadow-xl hover:scale-105 transition px-4 h-11 text-sm font-semibold"
      >
        <Lifebuoy size={16} weight="fill" />
        <span className="hidden sm:inline">Raise an issue</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-lg rounded-xl bg-[var(--bg-2)] border border-[var(--border-strong)] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="support-widget-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Raise an issue to Clariva</div>
                <h3 className="text-xl font-semibold tracking-tight mt-1" style={{ fontFamily: "var(--font-heading)" }}>How can we help?</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[var(--surface)] rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input data-testid="support-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="One-line summary" className="mt-1.5 glass-subtle border-[var(--border)]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="support-category" className="mt-1.5 glass-subtle border-[var(--border)] text-xs h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Bug</SelectItem>
                      <SelectItem value="question">Question</SelectItem>
                      <SelectItem value="feature">Feature request</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="support-severity" className="mt-1.5 glass-subtle border-[var(--border)] text-xs h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">What's happening?</Label>
                <Textarea data-testid="support-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Steps to reproduce, expected vs actual, screenshots links…" className="mt-1.5 glass-subtle border-[var(--border)] text-sm" />
              </div>
              <div className="text-[11px] text-[var(--text-3)]">We'll attach your page URL and browser for you automatically. Replies go to {user.email}.</div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={sending} data-testid="support-submit-btn" className="gap-2">
                <PaperPlaneTilt size={14} />
                {sending ? "Sending…" : "Send to Clariva"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
